// ============================================================================
// VISITOR OVERVIEW TAB — WorkItemDetailModal overview for visitor bookings
// (item_type='appointment', item_subtype='visitor')
// Renders the visitor booking card (PIN, QR, host, unit, approval/access
// status) above the standard UnifiedOverviewTab so all generic fields, tabs
// and RBAC behaviour stay identical to any other appointment.
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/visitor-management/bookings.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/visitor-management/bookings)
 */
import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import {
  UserCheck, Phone, Building2, Car, CreditCard, KeyRound,
  MapPin, Clock, ShieldCheck, ShieldX, Loader2, CalendarDays,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import UnifiedOverviewTab, {
  type UnifiedOverviewTabProps,
} from '@/components/work-items/UnifiedOverviewTab';
import { formatDateSAST, formatTimeSAST } from '@/lib/sastTime';

const QRCodeSVG = dynamic(
  () => import('qrcode.react').then((m) => m.QRCodeSVG),
  { ssr: false },
);

interface VisitorBookingRow {
  /** Visit work-item uuid (legacy serials resolve server-side). */
  id: number | string;
  pin: string | null;
  qr_data: string | null;
  visitor_name: string | null;
  visitor_phone: string | null;
  visitor_company: string | null;
  visitor_id_number: string | null;
  vehicle_registration: string | null;
  effective_date: string | null;
  visit_time: string | null;
  purpose: string | null;
  status: string | null;
  approval_status: string | null;
  source: string | null;
  expires_at: string | null;
  host_name?: string | null;
  pass_type?: string | null;
  goods_description?: string | null;
  max_uses?: number | null;
  uses_count?: number | null;
  visitor_count?: number | null;
  indemnity_accepted_at?: string | null;
}

const APPROVAL_BADGE: Record<string, { label: string; classes: string }> = {
  approved: { label: 'Approved', classes: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  auto_approved: { label: 'Auto-approved', classes: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  pending_host: { label: 'Awaiting host approval', classes: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  denied: { label: 'Denied', classes: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};

// Work-item visit statuses (item_type='visit').
const STATUS_BADGE: Record<string, { label: string; classes: string }> = {
  scheduled: { label: 'Booked', classes: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  pre_registered: { label: 'Pre-registered', classes: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  invited: { label: 'Invite sent', classes: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300' },
  checked_in: { label: 'Checked in', classes: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  on_site: { label: 'On site', classes: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  checked_out: { label: 'Checked out', classes: 'bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-300' },
  completed: { label: 'Checked out', classes: 'bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-300' },
  no_show: { label: 'No show', classes: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  denied: { label: 'Denied', classes: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  expired: { label: 'Expired', classes: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  cancelled: { label: 'Cancelled', classes: 'bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-400' },
};

function DetailRow({ icon: Icon, label, value }: {
  icon: React.ElementType;
  label: string;
  value?: string | null;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
      <div className="min-w-0">
        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
        <p className="font-medium text-gray-900 dark:text-gray-100 break-words">{value}</p>
      </div>
    </div>
  );
}

export default function VisitorOverviewTab(props: UnifiedOverviewTabProps) {
  const { workItem, isDark = false } = props;
  const [booking, setBooking] = useState<VisitorBookingRow | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = authService.getTokens()?.access_token;
        if (!token || !workItem?.id) return;
        const res = await fetch(
          `/api/v1/visitors/bookings?work_item_id=${encodeURIComponent(String(workItem.id))}&limit=1`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const json = await res.json().catch(() => null);
        if (!cancelled && json?.success && Array.isArray(json.data) && json.data.length > 0) {
          setBooking(json.data[0] as VisitorBookingRow);
        }
      } catch {
        // Booking card is best-effort — the standard overview still renders.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [workItem?.id]);

  const approval = booking?.approval_status ? APPROVAL_BADGE[booking.approval_status] : null;
  const status = booking?.status ? STATUS_BADGE[booking.status] : null;

  return (
    <div className="space-y-4">
      {/* Visitor booking card */}
      <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-blue-50/40'}`}>
        <div className="mb-3 flex items-center gap-2">
          <UserCheck className="h-4 w-4 text-blue-500" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Visitor Booking
          </h3>
          {approval && (
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${approval.classes}`}>
              {approval.label}
            </span>
          )}
          {status && (
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.classes}`}>
              {status.label}
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading booking…
          </div>
        ) : !booking ? (
          <p className="py-2 text-sm text-gray-500">
            No visitor booking record linked to this item.
          </p>
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
              <DetailRow icon={UserCheck} label="Visitor" value={booking.visitor_name} />
              <DetailRow icon={Phone} label="Phone" value={booking.visitor_phone} />
              <DetailRow icon={Building2} label="Company" value={booking.visitor_company} />
              <DetailRow icon={CreditCard} label="ID Number" value={booking.visitor_id_number} />
              <DetailRow icon={Car} label="Vehicle" value={booking.vehicle_registration} />
              <DetailRow icon={ShieldCheck} label="Host" value={booking.host_name} />
              <DetailRow
                icon={CalendarDays}
                label="Visit date"
                value={
                  booking.effective_date
                    ? `${formatDateSAST(booking.effective_date)}${
                        booking.visit_time ? ` at ${formatTimeSAST(booking.visit_time)}` : ''
                      }`
                    : null
                  }
              />
              <DetailRow
                icon={Clock}
                label="Code expires"
                value={booking.expires_at ? formatTimeSAST(booking.expires_at) : null}
              />
              <DetailRow icon={MapPin} label="Purpose" value={booking.purpose} />
              <DetailRow
                icon={booking.approval_status === 'denied' ? ShieldX : ShieldCheck}
                label="Source"
                value={booking.source ? booking.source.replace(/_/g, ' ') : null}
              />
              {booking.pass_type && booking.pass_type !== 'visitor' && (
                <DetailRow
                  icon={ShieldCheck}
                  label="Pass type"
                  value={booking.pass_type.replace(/_/g, ' ')}
                />
              )}
              {(booking.max_uses || 1) > 1 && (
                <DetailRow
                  icon={KeyRound}
                  label="Entries"
                  value={`${booking.uses_count || 0} of ${booking.max_uses} used`}
                />
              )}
              {booking.visitor_count && booking.visitor_count > 1 && (
                <DetailRow
                  icon={UserCheck}
                  label="Visitors"
                  value={String(booking.visitor_count)}
                />
              )}
              {booking.goods_description && (
                <DetailRow
                  icon={Building2}
                  label="Goods"
                  value={booking.goods_description}
                />
              )}
              {booking.indemnity_accepted_at && (
                <DetailRow
                  icon={ShieldCheck}
                  label="Indemnity accepted"
                  value={formatTimeSAST(booking.indemnity_accepted_at)}
                />
              )}
            </div>

            {booking.pin && (
              <div className="flex shrink-0 flex-col items-center gap-2">
                {booking.qr_data && (
                  <div className="rounded-lg bg-white p-2">
                    <QRCodeSVG value={booking.qr_data} size={104} />
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <KeyRound className="h-3.5 w-3.5 text-gray-400" />
                  <span className="font-mono text-lg font-semibold tracking-wider text-gray-900 dark:text-gray-100">
                    {booking.pin}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Standard appointment overview */}
      <UnifiedOverviewTab {...props} />
    </div>
  );
}
